import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import NotificationCenter from '../components/NotificationCenter';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const BRAID_IMAGE_COUNT = 19;

const LandingPage = ({
  isAuthenticated,
  setIsAuthenticated,
  setUserRole: setAppUserRole,
  setNotification,
}) => {
  const navigate = useNavigate();
  const [userRole, setLocalUserRole] = useState(null);
  const [randomServices, setRandomServices] = useState([]);
  const [servicesError, setServicesError] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);

  useEffect(() => {
    let isActive = true;

    const fetchServices = async () => {
      try {
        const response = await axios.get(apiUrl('services/'));
        const rows = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : [];

        const shuffled = [...rows].sort(() => 0.5 - Math.random());
        if (isActive) {
          setRandomServices(shuffled.slice(0, 6));
          setServicesError('');
        }
      } catch (error) {
        console.error('Error fetching featured services:', error?.response?.data || error.message);
        if (isActive) {
          setRandomServices([]);
          setServicesError('Unable to load featured services right now.');
        }
      }
    };

    fetchServices();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      localStorage.removeItem('userRole');
      setLocalUserRole(null);
      setAppUserRole?.(null);
      setIsAuthenticated(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await axios.get(apiUrl('userprofiles/me/'), {
          headers: { Authorization: `Bearer ${token}` },
        });

        const nextRole = response?.data?.role || null;
        setLocalUserRole(nextRole);
        localStorage.setItem('userRole', nextRole || '');
        setAppUserRole?.(nextRole);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Invalid or expired token, logging out:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userRole');
        setLocalUserRole(null);
        setAppUserRole?.(null);
        setIsAuthenticated(false);
      }
    };

    validateToken();
  }, [setIsAuthenticated, setAppUserRole]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    setLocalUserRole(null);
    setAppUserRole?.(null);
    setIsAuthenticated(false);
    navigate('/');
  };

  const heroActionHref = useMemo(() => {
    if (!isAuthenticated) return '/login';
    if (userRole === 'worker') return '/worker-dashboard';
    if (userRole === 'owner') return '/owner-dashboard';
    if (userRole === 'admin') return '/dashboard';
    return '/booking';
  }, [isAuthenticated, userRole]);

  const heroActionLabel = useMemo(() => {
    if (!isAuthenticated) return 'Get Started';
    if (userRole === 'worker') return 'Open Worker Dashboard';
    if (userRole === 'owner') return 'Open Owner Dashboard';
    if (userRole === 'admin') return 'Open Admin Dashboard';
    return 'Book an Appointment';
  }, [isAuthenticated, userRole]);

  const dashboardHref = userRole === 'owner'
    ? '/owner-dashboard'
    : userRole === 'admin'
      ? '/dashboard'
      : '/worker-dashboard';

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#F6F4F8] text-[#2E2E2E]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-[#E7D6EE] blur-3xl" />
        <div className="absolute top-32 right-0 h-80 w-80 rounded-full bg-[#F1E5C8] blur-3xl" />
      </div>

      <header className="sticky top-0 z-40 border-b border-white/50 bg-white/90 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-3 md:flex md:items-center md:justify-between md:gap-4">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-xl font-bold text-[#6D3B7E] sm:text-2xl">Scheduled Beauty</h1>
            <button
              type="button"
              className="relative inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-[#E5D4ED] bg-white text-xl text-[#4B2A56] shadow-sm md:hidden"
              aria-label={isMobileMenuOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
            >
              {isMobileMenuOpen ? '✕' : '☰'}
              {!isMobileMenuOpen && isAuthenticated && notificationUnreadCount > 0 ? (
                <span
                  className="absolute -right-2 -top-2 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[#EF5350] px-1 text-xs font-semibold text-white"
                  aria-label={`${notificationUnreadCount} unread notification${notificationUnreadCount === 1 ? '' : 's'}`}
                >
                  {notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}
                </span>
              ) : null}
            </button>
          </div>

          <div
            id="mobile-navigation"
            className={`${isMobileMenuOpen ? 'grid' : 'hidden'} mt-3 gap-3 border-t border-[#EEE6F1] pt-3 md:mt-0 md:flex md:items-center md:gap-3 md:border-0 md:pt-0`}
          >
            <nav aria-label="Main navigation">
              <ul className="grid grid-cols-2 gap-2 text-sm font-semibold text-[#4B2A56] sm:grid-cols-3 md:flex md:flex-wrap md:items-center">
                <li><Link to="/booking" className="flex min-h-11 items-center justify-center rounded-xl bg-[#F8F3FA] px-3 py-2 hover:bg-[#F4EBF8] md:min-h-0 md:rounded-full md:bg-transparent md:py-1">Book</Link></li>
                <li><Link to="/services" className="flex min-h-11 items-center justify-center rounded-xl bg-[#F8F3FA] px-3 py-2 hover:bg-[#F4EBF8] md:min-h-0 md:rounded-full md:bg-transparent md:py-1">Services</Link></li>
                <li><Link to="/myBookings" className="flex min-h-11 items-center justify-center rounded-xl bg-[#F8F3FA] px-3 py-2 hover:bg-[#F4EBF8] md:min-h-0 md:rounded-full md:bg-transparent md:py-1">My Bookings</Link></li>
                {isAuthenticated ? (
                  <li><Link to="/calendar" className="flex min-h-11 items-center justify-center rounded-xl bg-[#F8F3FA] px-3 py-2 hover:bg-[#F4EBF8] md:min-h-0 md:rounded-full md:bg-transparent md:py-1">Calendar</Link></li>
                ) : null}
                {isAuthenticated && ['worker', 'owner'].includes(userRole) ? (
                  <>
                    <li><Link to={dashboardHref} className="flex min-h-11 items-center justify-center rounded-xl bg-[#F8F3FA] px-3 py-2 hover:bg-[#F4EBF8] md:min-h-0 md:rounded-full md:bg-transparent md:py-1">Dashboard</Link></li>
                    <li><Link to="/worker-availability" className="flex min-h-11 items-center justify-center rounded-xl bg-[#F8F3FA] px-3 py-2 hover:bg-[#F4EBF8] md:min-h-0 md:rounded-full md:bg-transparent md:py-1">Availability</Link></li>
                    <li><Link to="/my-services" className="flex min-h-11 items-center justify-center rounded-xl bg-[#F8F3FA] px-3 py-2 hover:bg-[#F4EBF8] md:min-h-0 md:rounded-full md:bg-transparent md:py-1">My Services</Link></li>
                  </>
                ) : null}
                {isAuthenticated ? (
                  <li className="col-span-2 sm:col-span-3 md:col-auto">
                    <NotificationCenter
                      embedded
                      setNotification={setNotification}
                      onUnreadCountChange={setNotificationUnreadCount}
                    />
                  </li>
                ) : null}
              </ul>
            </nav>

            <div className="grid grid-cols-2 gap-2 md:flex md:items-center">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="flex min-h-11 items-center justify-center rounded-xl bg-[#F4EBF8] px-3 py-2 text-sm font-semibold text-[#4B2A56] hover:bg-[#EBDCF3] md:min-h-0 md:rounded-full md:py-1.5"
                  >
                    Profile
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="min-h-11 rounded-xl bg-[#EF5350] px-3 py-2 text-sm font-semibold text-white hover:bg-red-600 md:min-h-0 md:rounded-full md:py-1.5"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="col-span-2 min-h-11 rounded-xl bg-[#CDA349] px-4 py-2 text-sm font-semibold text-white hover:bg-[#b8913e] md:col-auto md:min-h-0 md:rounded-full md:py-1.5"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl space-y-5 px-3 py-5 sm:space-y-8 sm:px-4 sm:py-8">
        <section className="overflow-hidden rounded-3xl bg-gradient-to-r from-[#6D3B7E] via-[#7C4391] to-[#8A4AA3] p-5 text-white shadow-xl sm:p-6 md:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.25fr_0.75fr] lg:items-center">
            <div>
              <span className="inline-flex rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
                Beauty Booking Platform
              </span>
              <h2 className="mt-4 text-2xl font-bold leading-tight sm:text-3xl md:text-5xl">
                Find your next stylist or grow your beauty business with confidence.
              </h2>
              <p className="mt-4 max-w-2xl text-sm text-purple-100 md:text-base">
                Clients discover trusted services fast. Workers control schedules, appointments, and growth from one place.
              </p>

              <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap">
                <Link
                  to={heroActionHref}
                  className="min-h-11 rounded-full bg-[#CDA349] px-6 py-3 text-center text-sm font-semibold text-white hover:bg-[#b8913e]"
                >
                  {heroActionLabel}
                </Link>
                <Link
                  to="/services"
                  className="min-h-11 rounded-full bg-white/15 px-6 py-3 text-center text-sm font-semibold text-white backdrop-blur hover:bg-white/25"
                >
                  Explore Services
                </Link>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              <article className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur">
                <p className="text-2xl font-bold">24/7</p>
                <p className="mt-1 text-xs text-purple-100">Online scheduling anytime</p>
              </article>
              <article className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur">
                <p className="text-2xl font-bold">Fast</p>
                <p className="mt-1 text-xs text-purple-100">From discovery to booking in minutes</p>
              </article>
              <article className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur">
                <p className="text-2xl font-bold">Secure</p>
                <p className="mt-1 text-xs text-purple-100">Protected accounts and booking records</p>
              </article>
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-3">
          <article className="rounded-2xl bg-white p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-[#4B2A56]">For Clients</h3>
            <p className="mt-2 text-sm text-gray-600">Browse styles, compare services, and reserve appointments that fit your calendar.</p>
            <Link to="/booking" className="mt-4 inline-block text-sm font-semibold text-[#6D3B7E] hover:underline">Start booking</Link>
          </article>
          <article className="rounded-2xl bg-white p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-[#4B2A56]">For Workers</h3>
            <p className="mt-2 text-sm text-gray-600">Publish services, manage availability, and keep your schedule organized and visible.</p>
            <Link to="/worker-availability" className="mt-4 inline-block text-sm font-semibold text-[#6D3B7E] hover:underline">Manage availability</Link>
          </article>
          <article className="rounded-2xl bg-white p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-[#4B2A56]">For Returning Users</h3>
            <p className="mt-2 text-sm text-gray-600">Track bookings, payments, and updates in one timeline with actionable status controls.</p>
            <Link to="/myBookings" className="mt-4 inline-block text-sm font-semibold text-[#6D3B7E] hover:underline">Open my bookings</Link>
          </article>
        </section>

        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xl font-semibold text-[#4B2A56]">Style Inspiration</h3>
            <span className="rounded-full bg-[#F4EBF8] px-3 py-1 text-xs font-semibold text-[#6D3B7E]">
              Swipe to explore
            </span>
          </div>
          <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
            {Array.from({ length: BRAID_IMAGE_COUNT }).map((_, index) => (
              <img
                key={index}
                src={`/pictures/braid${index + 1}.png`}
                alt={`Braid style ${index + 1}`}
                className="h-64 w-52 flex-shrink-0 rounded-2xl object-cover shadow-sm transition hover:-translate-y-1 hover:shadow-md"
                loading="lazy"
              />
            ))}
          </div>
        </section>

        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xl font-semibold text-[#4B2A56]">Featured Services</h3>
            <Link to="/services" className="text-sm font-semibold text-[#6D3B7E] hover:underline">See all services</Link>
          </div>

          {servicesError ? <InlineFeedback type="warning" message={servicesError} className="mt-4" /> : null}

          {randomServices.length > 0 ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {randomServices.map((service) => (
                <article
                  key={service.id}
                  className="group rounded-2xl border border-gray-200 bg-[#FCFBFE] p-4 transition hover:-translate-y-0.5 hover:border-[#E5D4ED] hover:shadow-sm"
                >
                  <p className="text-base font-semibold text-[#4B2A56]">{service.name}</p>
                  <p className="mt-2 line-clamp-3 text-sm text-gray-600">{service.description || 'Professional beauty service with personalized care.'}</p>
                </article>
              ))}
            </div>
          ) : (
            !servicesError ? <p className="mt-4 text-sm text-gray-500">No services available at the moment.</p> : null
          )}
        </section>

        <section className="rounded-3xl bg-[#1F1328] p-6 text-white shadow-xl md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-2xl font-bold">Ready to transform your booking experience?</h3>
              <p className="mt-2 text-sm text-purple-100">Join Scheduled Beauty today and make every appointment feel effortless.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/booking" className="rounded-full bg-[#CDA349] px-5 py-2 text-sm font-semibold text-white hover:bg-[#b8913e]">
                Book now
              </Link>
              <Link to="/login" className="rounded-full bg-white/15 px-5 py-2 text-sm font-semibold text-white hover:bg-white/25">
                Sign in
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/50 bg-white/90 py-5 text-center text-sm text-gray-600">
        <p>&copy; 2026 Scheduled Beauty. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LandingPage;
